// src/lib/api.js
import axios from "axios";

/* ---------- Config ---------- */
/**
 * In productie (deploy pe subdomenii):
 *  - recomandat: VITE_API_BASE_URL=/api
 *    (API-ul este servit pe acelasi host ca UI: https://<slug>.appointiq.ro/api)
 *
 * In dev poti lasa ne-setat, caz in care foloseste "/api"
 * (presupunand ca ai un proxy din Vite catre backend).
 */
function computeApiPrefix() {
  const env = import.meta.env.VITE_API_BASE_URL;
  if (env) return String(env).replace(/\/+$/, "");
  return "/api";
}

const RAW_API_PREFIX = computeApiPrefix();
export const API_PREFIX = RAW_API_PREFIX.replace(/\/+$/, "");

/* ---------- Token (P1+): access in memory, refresh in HttpOnly cookie ---------- */
/**
 * Access token: doar în memorie (nu persistăm în localStorage/sessionStorage).
 * Refresh token: cookie HttpOnly (setat de backend la login/refresh).
 *
 * Compat / migrare:
 *  - Dacă găsim refresh token vechi în storage (din P0/P1), îl folosim o singură dată
 *    pe /auth/token/refresh/ ca să setăm cookie-ul, apoi îl ștergem.
 */
const LEGACY_KEYS = {
  access: ["access_token", "accessToken"],
  refresh: ["refresh_token", "refreshToken"],
};

function consumeFromStorages(keys) {
  try {
    for (const k of keys) {
      const vSess = sessionStorage.getItem(k);
      const vLocal = localStorage.getItem(k);
      const v = vSess || vLocal;
      if (v) {
        // cleanup
        sessionStorage.removeItem(k);
        localStorage.removeItem(k);
        return v;
      }
      // cleanup anyway
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
  return "";
}

let accessToken = "";
let legacyRefreshToken = "";

/* ---------- Business helpers ---------- */
const isIp = (hostname) => /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

/**
 * Regula:
 * - dacă suntem pe localhost / IP → folosim slug-ul din storage (current_business_slug)
 * - altfel deducem din host:
 *    dev: <slug>.localhost
 *    prod: <slug>.appointiq.ro
 */
function getBusinessSlug() {
  try {
    const host = window?.location?.hostname || "";

    // localhost / IP -> din storage
    if (!host || host === "localhost" || isIp(host) || host.startsWith("127.")) {
      return String(localStorage.getItem("current_business_slug") || "").trim();
    }

    // dev: "test-nou.localhost"
    if (host.endsWith(".localhost")) {
      return host.split(".")[0] || "";
    }

    // prod: "<slug>.appointiq.ro" (sau "<slug>.something.appointiq.ro")
    const parts = host.split(".").filter(Boolean);
    if (parts.length >= 3 && host.endsWith("appointiq.ro")) {
      const first = parts[0] || "";
      if (first && first !== "www" && first !== "appointiq") return first;
    }

    return "";
  } catch {
    return "";
  }
}

function applyBusinessHeaders(config) {
  config.headers = config.headers || {};

  // pune slug dacă lipsește
  const slug = getBusinessSlug();
  if (
    slug &&
    !config.headers["X-Business-Subdomain"] &&
    !config.headers["X-Business-Slug"]
  ) {
    config.headers["X-Business-Subdomain"] = slug;
  }

  // location / location-scope (dacă există)
  const locScopeId = localStorage.getItem("current_location_scope_id");
  const locId = localStorage.getItem("current_location_id");

  // dacă backend-ul tău folosește scope, trimite-l mereu
  if (locScopeId && !config.headers["X-Location-Scope-Id"]) {
    config.headers["X-Location-Scope-Id"] = String(locScopeId);
  }

  // fallback: location id simplu (dacă îl folosești în unele endpoint-uri)
  if (locId && !config.headers["X-Location-Id"]) {
    config.headers["X-Location-Id"] = String(locId);
  }

  // pentru endpoint-uri /b/* pune business id
  const url = String(config.url || "");

  const isBusinessEndpoint =
    url.startsWith("/b/") || url.startsWith("b/") || url.includes("/b/");

  const isRefreshOrLogout =
    url.includes("/auth/token/refresh") || url.includes("/auth/logout");

  const isLogin = url.includes("/auth/login");

  const shouldAttachBusinessId = (isBusinessEndpoint || isRefreshOrLogout) && !isLogin;

  if (shouldAttachBusinessId) {
    const bizId = localStorage.getItem("current_business_id");
    if (bizId && !config.headers["X-Business-Id"]) {
      config.headers["X-Business-Id"] = String(bizId);
    }
  }

  return config;
}

/* ---------- Axios instance ---------- */
const API_HOST = (() => {
  const env = import.meta.env.VITE_API_URL;
  if (env) return String(env).replace(/\/+$/, "");

  const host = window?.location?.hostname || "";
  const isLocal = host === "localhost" || host.startsWith("127.") || /^\d{1,3}(\.\d{1,3}){3}$/.test(host);

  // Dev default: backend local
  if (isLocal) return "";

  // Prod default: same-origin (https://<slug>.appointiq.ro/api)
  return "";
})();

const api = axios.create({
  baseURL: `${API_HOST}${API_PREFIX}`,
  withCredentials: true,
});



/* ---------- Access token API ---------- */
export function getAccessToken() {
  return accessToken || "";
}

export function setAccessToken(token) {
  accessToken = token ? String(token) : "";
  if (accessToken) {
    api.defaults.headers.Authorization = `Bearer ${accessToken}`;
  } else if (api?.defaults?.headers) {
    delete api.defaults.headers.Authorization;
  }
}

export function clearAccessToken() {
  setAccessToken("");
}

function clearLegacyStorage() {
  try {
    [...LEGACY_KEYS.access, ...LEGACY_KEYS.refresh].forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
  } catch {
    // ignore
  }
}

/* ---------- Legacy migration (one-time) ---------- */
(function migrateLegacyOnce() {
  const legacyAccess = consumeFromStorages(LEGACY_KEYS.access);
  if (legacyAccess) setAccessToken(legacyAccess);

  legacyRefreshToken = consumeFromStorages(LEGACY_KEYS.refresh) || "";

  // ensure nothing remains
  clearLegacyStorage();
})();

/* ---------- Request interceptor ---------- */
api.interceptors.request.use((config) => {
  // business headers (slug + business id where needed)
  applyBusinessHeaders(config);

  // bearer token (in-memory)
  const t = getAccessToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }

  return config;
});

/* ---------- Refresh helper ---------- */
export async function refreshAccessToken() {
  // dacă avem refresh legacy în memorie, îl trimitem o singură dată ca să setăm cookie-ul
  const payload = legacyRefreshToken ? { refresh: legacyRefreshToken } : {};

  const refreshConfig = {
    withCredentials: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  };
  applyBusinessHeaders(refreshConfig);

  const { data } = await api.post(
    `/auth/token/refresh/`,
    payload,
    refreshConfig
  );


  const newAccess = data?.access;
  if (!newAccess) throw new Error("No access in refresh response");

  // după prima refresh reușită, drop legacy refresh
  legacyRefreshToken = "";

  setAccessToken(newAccess);
  return newAccess;
}

/* ---------- Auto refresh on 401 ---------- */
let isRefreshing = false;
let queued = [];


function isSubscriptionGateError(error) {
  const status = error?.response?.status;
  if (status !== 403) return false;

  const data = error?.response?.data;
  const detail =
    typeof data === "string"
      ? data
      : (data?.detail || data?.message || "");

  const code =
    typeof data === "object" && data
      ? String(data.code || data.error || "")
      : "";

  // backend compat (plain message)
  const detailHit =
    typeof detail === "string" &&
    /abonament.*(expirat|suspendat)|subscription/i.test(detail);

  const codeHit =
    code === "subscription_inactive" ||
    code === "subscription_blocked" ||
    code === "billing_inactive";

  const statusHit =
    typeof data === "object" &&
    data &&
    ["blocked", "canceled"].includes(String(data.subscription_status || "").toLowerCase());

  return Boolean(detailHit || codeHit || statusHit);
}

function redirectToSuspendedIfNeeded() {
  try {
    const p = window.location.pathname || "";
    if (!p.startsWith("/app")) return;
    if (p.startsWith("/app/billing") || p.startsWith("/app/subscription-suspended")) return;
    window.location.href = "/app/subscription-suspended";
  } catch {
    // ignore
  }
}

function isAuthUrl(url) {
  const u = String(url || "");
  return (
    u.includes("/auth/login") ||
    u.includes("/auth/token/refresh") ||
    u.includes("/auth/logout")
  );
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;

    // Subscription gate: dacă primim 403 pentru abonament expirat/suspendat,
    // scoatem userul din flow-ul normal și îl ducem pe paywall.
    if (isSubscriptionGateError(error)) {
      redirectToSuspendedIfNeeded();
      return Promise.reject(error);
    }


    if (status !== 401 || original._retried || isAuthUrl(original.url)) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queued.push({ resolve, reject, original });
      });
    }

    original._retried = true;
    isRefreshing = true;

    try {
      const newAccess = await refreshAccessToken();

      // rezolvă coada
      queued.forEach(({ resolve, original: o }) => {
        o.headers = { ...(o.headers || {}), Authorization: `Bearer ${newAccess}` };
        resolve(api(o));
      });
      queued = [];

      // retry requestul original
      original.headers = {
        ...(original.headers || {}),
        Authorization: `Bearer ${newAccess}`,
      };
      return api(original);
    } catch (e) {
      queued.forEach(({ reject }) => reject(e));
      queued = [];

      clearAccessToken();
      clearLegacyStorage();

      // best-effort: clear refresh cookie
      try {
        await api.post(`/auth/logout/`, {}, { withCredentials: true });
      } catch {
        // ignore
      }

      window.location.href = "/app/login";
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
    }
  }
);

/* ---------- Helpers ---------- */
export const withBusiness = (path) => {
  const bid = window.localStorage.getItem("current_business_id");
  if (!bid) throw new Error("Nu există business selectat în acest moment.");
  return path.replace(":businessId", bid);
};

// Login – endpoint: /auth/login
export async function login(username, password) {
  const u = Array.isArray(username) ? username[0] : username;
  const p = Array.isArray(password) ? password[0] : password;

  const { data } = await api.post(
    "/auth/login/",
    { username: String(u ?? ""), password: String(p ?? "") },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );

  const access = data?.access || data?.access_token;
  if (access) setAccessToken(access);

  // Business curent (pentru /b/*)
  if (data?.business_id) {
    localStorage.setItem("current_business_id", String(data.business_id));
  } else {
    localStorage.removeItem("current_business_id");
  }

  // IMPORTANT: pe localhost/IP avem nevoie de slug în storage pentru header
  if (data?.business_slug) {
    localStorage.setItem("current_business_slug", String(data.business_slug));
  }

  return data;
}

export function logout() {
  // best-effort: clear refresh cookie
  api.post("/auth/logout/").catch(() => { });
  clearAccessToken();
  clearLegacyStorage();

  localStorage.removeItem("current_business_id");
  localStorage.removeItem("current_business_slug");
  localStorage.removeItem("current_location_id");
  localStorage.removeItem("current_location_scope_id");
}

/* ---------- Appointments (Dashboard + Calendar) ---------- */
export async function getAppointments({ from, to, locationId } = {}) {
  const toIso = (v) => (typeof v === "string" ? v : (v || new Date()).toISOString());

  const fromDate =
    from || new Date(new Date().setUTCHours(0, 0, 0, 0)).toISOString();

  const toDate =
    to || new Date(Date.parse(fromDate) + 24 * 3600 * 1000).toISOString();

  const params = {
    from: toIso(fromDate),
    to: toIso(toDate),
    ...(locationId ? { location_id: String(locationId) } : {}),
  };

  const { data } = await api.get("/b/appointments", { params });

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

/* ---------- Password reset ---------- */
export function requestPasswordReset(email) {
  return api.post(`/auth/password/forgot/`, { email });
}

export function confirmPasswordReset({ uid, token, new_password }) {
  return api.post(`/auth/password/reset/`, { uid, token, new_password });
}

/* ---------- Business Settings API ---------- */
function requireBusinessId(explicitId) {
  const bid = explicitId ?? window.localStorage.getItem("current_business_id");
  if (!bid) throw new Error("Nu există business selectat.");
  return String(bid);
}

export async function getBusinessSettings(businessId) {
  const bid = requireBusinessId(businessId);
  const { data } = await api.get(`/b/settings`, {
    headers: { "X-Business-Id": bid },
  });
  return data;
}

export async function putBusinessSettings(payload, businessId) {
  const bid = requireBusinessId(businessId);
  const { data } = await api.put(`/b/settings`, payload, {
    headers: { "X-Business-Id": bid },
  });
  return data;
}

export async function patchBusinessSettings(partialPayload, businessId) {
  const bid = requireBusinessId(businessId);
  const { data } = await api.patch(`/b/settings`, partialPayload, {
    headers: { "X-Business-Id": bid },
  });
  return data;
}

/* ---------- Staff (Business Employees) ---------- */
export async function getStaff(businessId) {
  const bid = requireBusinessId(businessId);
  const { data } = await api.get("/b/staff", {
    headers: { "X-Business-Id": bid },
  });
  return Array.isArray(data) ? data : [];
}

export async function getStaffMember(staffId, businessId) {
  const bid = requireBusinessId(businessId);
  const { data } = await api.get(`/b/staff/${staffId}`, {
    headers: { "X-Business-Id": bid },
  });
  return data;
}

export async function createStaff(payload, businessId) {
  const bid = requireBusinessId(businessId);
  const { data } = await api.post("/b/staff", payload, {
    headers: { "X-Business-Id": bid },
  });
  return data;
}

export async function updateStaff(staffId, payload, businessId) {
  const bid = requireBusinessId(businessId);
  const { data } = await api.patch(`/b/staff/${staffId}`, payload, {
    headers: { "X-Business-Id": bid },
  });
  return data;
}

export async function deleteStaff(staffId, businessId) {
  const bid = requireBusinessId(businessId);
  await api.delete(`/b/staff/${staffId}`, {
    headers: { "X-Business-Id": bid },
  });
}

export default api;
